import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import { DeleteOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { bg_button, bg_shadow } from "constant/css";
import GetMerchants from "functions/admin/getMerchants";
import {
  clearRedisCache,
  getRedisCacheTypes,
  inspectRedisCache,
} from "functions/admin/redisCache";

const GLOBAL_TYPE_IDS = new Set([
  "khanToken",
  "vatDistricts",
  "posesAll",
]);

function buildItems(selectedTypes, typesMeta, form) {
  const items = [];
  for (const typeId of selectedTypes) {
    const meta = typesMeta.find((t) => t.id === typeId);
    if (!meta) continue;
    if (meta.params.length === 0) {
      items.push({ type: typeId });
      continue;
    }
    const payload = { type: typeId };
    for (const p of meta.params) {
      payload[p.name] = form[p.name];
    }
    items.push(payload);
  }
  return items;
}

function validateForm(selectedTypes, typesMeta, form) {
  for (const typeId of selectedTypes) {
    const meta = typesMeta.find((t) => t.id === typeId);
    if (!meta) continue;
    for (const p of meta.params) {
      if (!p.required) continue;
      const v = form[p.name];
      if (v === undefined || v === null || v === "") {
        return `${meta.label}: ${p.name} оруулна уу.`;
      }
    }
  }
  return null;
}

const RedisCacheAdmin = () => {
  const [typesMeta, setTypesMeta] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [selectedGlobal, setSelectedGlobal] = useState([]);
  const [selectedScoped, setSelectedScoped] = useState([]);
  const [merchantId, setMerchantId] = useState(null);
  const [posId, setPosId] = useState("");
  const [register, setRegister] = useState("");
  const [inspectRows, setInspectRows] = useState([]);
  const [loadingInspect, setLoadingInspect] = useState(false);
  const [loadingClear, setLoadingClear] = useState(false);

  useEffect(() => {
    (async () => {
      const [types, merchantRes] = await Promise.all([
        getRedisCacheTypes(),
        GetMerchants(),
      ]);
      setTypesMeta(types || []);
      setMerchants(merchantRes?.data || []);
    })();
  }, []);

  const globalOptions = useMemo(
    () =>
      typesMeta
        .filter((t) => GLOBAL_TYPE_IDS.has(t.id))
        .map((t) => ({ label: t.label, value: t.id })),
    [typesMeta]
  );

  const scopedOptions = useMemo(
    () =>
      typesMeta
        .filter((t) => !GLOBAL_TYPE_IDS.has(t.id))
        .map((t) => ({ label: t.label, value: t.id })),
    [typesMeta]
  );

  const selectedTypes = useMemo(
    () => [...selectedGlobal, ...selectedScoped],
    [selectedGlobal, selectedScoped]
  );

  const formValues = useMemo(
    () => ({
      merchantId,
      posId: posId ? Number(posId) : undefined,
      register: register.trim() || undefined,
    }),
    [merchantId, posId, register]
  );

  const buildPayload = useCallback(() => {
    return buildItems(selectedTypes, typesMeta, formValues);
  }, [selectedTypes, typesMeta, formValues]);

  const onInspect = async () => {
    if (selectedTypes.length === 0) {
      message.warning("Дор хаяж нэг cache сонгоно уу.");
      return;
    }
    const err = validateForm(selectedTypes, typesMeta, formValues);
    if (err) {
      message.error(err);
      return;
    }
    setLoadingInspect(true);
    try {
      const items = buildPayload();
      const res = await inspectRedisCache(items);
      if (!res?.success) {
        message.error(res?.error?.message || "Шалгаж чадсангүй.");
        return;
      }
      setInspectRows(res.data || []);
    } finally {
      setLoadingInspect(false);
    }
  };

  const onClear = async () => {
    const err = validateForm(selectedTypes, typesMeta, formValues);
    if (err) {
      message.error(err);
      return;
    }
    setLoadingClear(true);
    try {
      const items = buildPayload();
      const res = await clearRedisCache(items);
      if (!res?.success) {
        message.error(res?.error?.message || "Устгаж чадсангүй.");
        return;
      }
      const deleted = (res.data || []).filter((r) => r.deleted).length;
      message.success(`${deleted} түлхүүр устгагдлаа.`);
      await onInspect();
    } finally {
      setLoadingClear(false);
    }
  };

  const columns = [
    {
      title: "Түлхүүр",
      dataIndex: "key",
      key: "key",
      render: (key) => (
        <code className="text-xs break-all">{key}</code>
      ),
    },
    {
      title: "Байгаа эсэх",
      dataIndex: "exists",
      key: "exists",
      width: 110,
      render: (exists) =>
        exists ? <Tag color="green">Тийм</Tag> : <Tag>Үгүй</Tag>,
    },
    {
      title: "TTL (сек)",
      dataIndex: "ttlSeconds",
      key: "ttlSeconds",
      width: 100,
      render: (ttl) =>
        ttl == null || ttl < 0 ? "—" : ttl === -1 ? "∞" : ttl,
    },
    {
      title: "Урьдчилсан",
      dataIndex: "preview",
      key: "preview",
      ellipsis: true,
      render: (preview) =>
        preview ? (
          <Tooltip title={preview}>
            <span className="text-xs text-gray-600">{preview}</span>
          </Tooltip>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <Card
      className={`${bg_shadow} mb-6`}
      title={
        <span className="text-[#32B7C2] font-semibold">
          Redis cache цэвэрлэх
        </span>
      }
    >
      <p className="text-sm text-gray-600 mb-4">
        Зөвхөн системд бүртгэгдсэн cache-үүдийг сонгож шалгах, устгах боломжтой.
        Жишээ нь мерчантын{" "}
        <code className="text-xs">billingStatus</code> cache устгавал дараагийн
        шалгалт DB-ээс дахин уншина.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="font-medium mb-2">Ерөнхий cache</div>
          <Checkbox.Group
            options={globalOptions}
            value={selectedGlobal}
            onChange={setSelectedGlobal}
            className="flex flex-col gap-2"
          />

          <div className="font-medium mt-6 mb-2">Параметртэй cache</div>
          <Checkbox.Group
            options={scopedOptions}
            value={selectedScoped}
            onChange={setSelectedScoped}
            className="flex flex-col gap-2 mb-4"
          />

          {selectedScoped.includes("merchantBillingStatus") && (
            <div className="mb-3">
              <div className="text-xs text-gray-500 mb-1">Мерчант</div>
              <Select
                showSearch
                allowClear
                placeholder="Мерчант сонгох"
                optionFilterProp="label"
                className="w-full"
                value={merchantId}
                onChange={setMerchantId}
                options={merchants.map((m) => ({
                  value: m.id,
                  label: `#${m.id} ${m.name || m.slogan || ""}`,
                }))}
              />
            </div>
          )}

          {selectedScoped.includes("posSingle") && (
            <div className="mb-3">
              <div className="text-xs text-gray-500 mb-1">POS ID</div>
              <Input
                placeholder="pos ID"
                value={posId}
                onChange={(e) => setPosId(e.target.value.replace(/\D/g, ""))}
              />
            </div>
          )}

          {selectedScoped.includes("vatMerchant") && (
            <div className="mb-3">
              <div className="text-xs text-gray-500 mb-1">
                VAT merchant register
              </div>
              <Input
                placeholder="Регистр"
                value={register}
                onChange={(e) => setRegister(e.target.value)}
              />
            </div>
          )}

          <Space wrap className="mt-4">
            <Button
              icon={<SearchOutlined />}
              loading={loadingInspect}
              onClick={onInspect}
              className={`${bg_button} text-white border-none`}
            >
              Шалгах
            </Button>
            <Popconfirm
              title="Сонгосон Redis түлхүүрүүдийг устгах уу?"
              okText="Тийм"
              cancelText="Үгүй"
              onConfirm={onClear}
              disabled={selectedTypes.length === 0}
            >
              <Button
                danger
                type="primary"
                icon={<DeleteOutlined />}
                loading={loadingClear}
                disabled={selectedTypes.length === 0}
              >
                Цэвэрлэх
              </Button>
            </Popconfirm>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setInspectRows([]);
                setSelectedGlobal([]);
                setSelectedScoped([]);
                setMerchantId(null);
                setPosId("");
                setRegister("");
              }}
            >
              Сонголт цэвэрлэх
            </Button>
          </Space>
        </div>

        <div>
          <div className="font-medium mb-2">Шалгалтын үр дүн</div>
          <Table
            size="small"
            rowKey="key"
            columns={columns}
            dataSource={inspectRows}
            pagination={false}
            locale={{ emptyText: "«Шалгах» дарж cache-ийн байдал хараарай" }}
            scroll={{ x: true }}
          />
        </div>
      </div>
    </Card>
  );
};

export default RedisCacheAdmin;
